import { InlineButton } from '@/components/buttons/inline-button';
import { LoginHeader } from '@/components/headings/login-header';
import { OtpPrimary } from '@/components/otp/otp-primary';
import { useTranslation } from '@/hooks/use-translation';
import { parsePhoneNumber } from '@/lib/countries';
import { consumeOAuthConsentReturn } from '@/lib/oauth-consent-return';
import amplitudeService from '@/services/amplitude';
import { supabase } from '@/services/supabase';
import { createFileRoute, redirect, useRouter } from '@tanstack/react-router';
import { fallback, zodValidator } from '@tanstack/zod-adapter';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';

const otpSchema = z.object({
  phone: fallback(z.string(), ''),
  email: fallback(z.string(), ''),
});

export const Route = createFileRoute('/login/otp-code')({
  component: RouteComponent,
  validateSearch: zodValidator(otpSchema),
  beforeLoad: ({ search }) => {
    const phone = parsePhoneNumber(search.phone);
    const email = search.email;

    if (!phone?.isValid() && !email) {
      return redirect({ to: '/login' });
    }
  },
});

function RouteComponent() {
  const [_otp, setOtp] = useState<string>('');
  const [_isLoading, setIsLoading] = useState(false);

  const { t } = useTranslation('desktop_login');
  const router = useRouter();
  const { phone, email } = Route.useSearch();
  const isEmailOtp = !!email;
  const contactMethod = isEmailOtp ? email : phone;

  const handleOtpComplete = async (otp: string) => {
    setIsLoading(true);

    amplitudeService.logEvent(
      isEmailOtp ? 'Authentication Email OTP Filled' : 'Authentication Phone OTP Filled',
    );

    const { error } = await supabase.auth.verifyOtp(
      isEmailOtp
        ? {
            email: email as string,
            token: otp,
            type: 'email',
          }
        : {
            phone: phone,
            token: otp,
            type: 'sms',
          },
    );

    setIsLoading(false);

    amplitudeService.logEvent(
      isEmailOtp ? 'Authentication Email OTP Results' : 'Authentication Phone OTP Results',
      {
        Result: error ? 'Fail' : 'Success',
      },
    );

    if (error) {
      toast.error(error.message, {
        position: 'top-center',
        style: {
          // @ts-expect-error
          right: !0,
        },
      });
    } else {
      amplitudeService.logEvent('Signed In', {
        Method: isEmailOtp ? 'email' : 'phone',
      });
      const consentReturn = consumeOAuthConsentReturn();
      if (consentReturn) {
        router.navigate({ href: consentReturn, replace: true });
      } else {
        router.navigate({ to: '/' });
      }
    }
  };

  const handleResendOtp = async () => {
    const { error } = await supabase.auth.signInWithOtp(
      isEmailOtp
        ? { email: email as string }
        : {
            phone,
          },
    );

    if (error) {
      toast.error(error.message, {
        position: 'top-center',
        style: {
          // @ts-expect-error
          right: !0,
        },
      });
    } else {
      toast.success(t('otp-code.resend-success'), {
        position: 'top-center',
        style: {
          // @ts-expect-error
          right: !0,
        },
      });
    }
  };

  const handleGoBack = async () => {
    await supabase.auth.signOut({ scope: 'local' });
    router.navigate({
      to: '/login',
      replace: true,
    });
  };

  useEffect(() => {
    amplitudeService.logEvent(
      isEmailOtp ? 'Authentication Email OTP Displayed' : 'Authentication Phone OTP Displayed',
    );
  }, [isEmailOtp]);

  return (
    <>
      <div className="overflow:visible duration-500 animate-in slide-in-from-right-1/6">
        <LoginHeader
          title={t('otp-code.title')}
          description={t('otp-code.description', { contactMethod })}
        />

        <div className="flex flex-col gap-8 items-center justify-center">
          <OtpPrimary onComplete={handleOtpComplete} length={6} onChange={setOtp} />

          <div className="flex gap-4">
            <InlineButton
              onClick={() => handleResendOtp()}
              className="text-body-14 no-underline text-primary/70"
            >
              {t('otp-code.resend')}
            </InlineButton>
            <InlineButton
              onClick={() => handleGoBack()}
              className="text-body-14 no-underline text-primary/70"
            >
              {t('otp-code.go-back')}
            </InlineButton>
          </div>

          {/* <PrimaryButton
              isLoading={isLoading}
              disabled={otp.length !== 6}
              onClick={() => handleOtpComplete(otp)}
            >
              {t('otp-code.button')}
            </PrimaryButton> */}
        </div>
      </div>
    </>
  );
}
